import type { TelemetrySample } from "./types";

/**
 * How a device is connected, in words staff read at a glance: "Wi-Fi · Shop-Guest",
 * "Mobile · 5G · Cosmote", "Ethernet". Agents before 1.1.0 report only the kind of
 * network and its signal; the name, band, generation and address come with 1.1.0.
 */

export type ConnectionKind = "wifi" | "cellular" | "ethernet" | "vpn" | "none" | "other";

export type SignalQuality = "Excellent" | "Good" | "Fair" | "Weak";

export interface Connection {
  kind: ConnectionKind;
  /** "Wi-Fi", "Mobile", "Ethernet", "VPN", "No network", "Other". */
  label: string;
  /** The Wi-Fi network's name, or the mobile carrier. */
  name: string | null;
  /** "5G" / "4G" on mobile. */
  generation: string | null;
  /** "5 GHz · 433 Mb/s" on Wi-Fi. */
  link: string | null;
  signal: { dbm: number; quality: SignalQuality } | null;
  /** The device's own address on that network. */
  localIp: string | null;
}

const LABELS: Record<ConnectionKind, string> = {
  wifi: "Wi-Fi",
  cellular: "Mobile",
  ethernet: "Ethernet",
  vpn: "VPN",
  none: "No network",
  other: "Other",
};

function kindOf(type: string | null | undefined): ConnectionKind | null {
  if (!type) return null;
  const value = type.toLowerCase();
  if (value === "wifi") return "wifi";
  if (value === "ethernet") return "ethernet";
  if (value === "vpn") return "vpn";
  if (value === "none") return "none";
  // Some agents name the radio instead: "lte", "5g", "mobile".
  if (["cellular", "mobile", "lte", "nr", "5g", "4g", "3g", "2g", "umts", "hspa"].includes(value)) {
    return "cellular";
  }
  return "other";
}

export function wifiBand(mhz: number | null | undefined): string | null {
  if (!mhz || mhz <= 0) return null;
  if (mhz < 3000) return "2.4 GHz";
  if (mhz < 5925) return "5 GHz";
  return "6 GHz";
}

/**
 * Wi-Fi RSSI and mobile RSRP read differently: -70 dBm is a usable Wi-Fi link but a
 * strong mobile one. Thresholds follow the common Android/carrier bars.
 */
export function signalQuality(dbm: number, kind: ConnectionKind): SignalQuality {
  const [excellent, good, fair] = kind === "cellular" ? [-85, -100, -110] : [-55, -67, -75];
  if (dbm >= excellent) return "Excellent";
  if (dbm >= good) return "Good";
  if (dbm >= fair) return "Fair";
  return "Weak";
}

export function describeConnection(sample: TelemetrySample | null | undefined): Connection | null {
  const kind = kindOf(sample?.network_type);
  if (!sample || !kind) return null;
  const dbm = sample.network_strength_dbm;
  const link =
    kind === "wifi"
      ? [
          wifiBand(sample.wifi_frequency_mhz),
          sample.wifi_link_mbps ? `${sample.wifi_link_mbps} Mb/s` : null,
        ]
          .filter(Boolean)
          .join(" · ") || null
      : null;
  return {
    kind,
    label: LABELS[kind],
    name: (kind === "wifi" ? sample.wifi_ssid : kind === "cellular" ? sample.carrier : null) ?? null,
    generation: kind === "cellular" ? (sample.cellular_generation ?? null) : null,
    link,
    signal: typeof dbm === "number" ? { dbm, quality: signalQuality(dbm, kind) } : null,
    localIp: sample.local_ip ?? null,
  };
}

/** One line: "Wi-Fi · Shop-Guest", "Mobile · 5G · Cosmote", "Ethernet". */
export function connectionSummary(connection: Connection | null): string | null {
  if (!connection) return null;
  return [connection.label, connection.generation, connection.name].filter(Boolean).join(" · ");
}

/** Whether the reading carries the details agents 1.1.0+ send. */
export function hasConnectionDetails(sample: TelemetrySample | null | undefined): boolean {
  return Boolean(
    sample &&
      (sample.local_ip ||
        sample.wifi_ssid ||
        sample.wifi_frequency_mhz ||
        sample.cellular_generation ||
        sample.carrier),
  );
}
